import { DeviceKind, getDeviceKind } from "./DeviceUtil";

/**
 * REQ-313 추가 요구(2026-09-21) — 운영팀 알림(ASMT-PROCTOR-16)에 싣는 응시자 접속 환경.
 *
 * 브라우저·OS 에 따라 운영팀의 안내 방향(권한 설정 경로, 공유 선택창 모양)이 달라진다.
 * 서버는 User-Agent 를 보지 않고(CDN 경유 시 전달도 보장되지 않는다), UA 만으로는 Windows 10/11 이나
 * 실제 macOS 버전을 알 수 없다(Chrome 의 UA 축소). 그래서 브라우저가 아는 가장 정확한 값을 모아
 * **코드**로 보내고, 사람이 읽는 라벨은 서버가 만든다 — 클라이언트 문자열을 운영 채널에 그대로
 * 싣지 않는다(BR2), 변환 주체는 백엔드다(BR7).
 */
export enum ClientBrowser {
  CHROME = "CHROME",
  EDGE = "EDGE",
  WHALE = "WHALE",
  OPERA = "OPERA",
  SAMSUNG_INTERNET = "SAMSUNG_INTERNET",
  FIREFOX = "FIREFOX",
  SAFARI = "SAFARI",
  /** 자기 브랜드를 밝히지 않는 Chromium 계열 (Proctive 의 Electron 웹뷰 포함). */
  CHROMIUM = "CHROMIUM",
  OTHER = "OTHER",
}

export enum ClientOs {
  WINDOWS = "WINDOWS",
  MACOS = "MACOS",
  IOS = "IOS",
  IPADOS = "IPADOS",
  ANDROID = "ANDROID",
  CHROMEOS = "CHROMEOS",
  LINUX = "LINUX",
  OTHER = "OTHER",
}

export interface ClientEnvironment {
  browser: ClientBrowser;
  browserVersion?: string;
  os: ClientOs;
  /** 알 수 없으면 비워 둔다 — 추정값을 싣지 않는다. */
  osVersion?: string;
  deviceKind: DeviceKind;
  /** Proctive 데스크톱 앱 버전. 일반 브라우저에서는 없다. */
  appVersion?: string;
}

/** User-Agent Client Hints 중 쓰는 부분만 (lib.dom 에 아직 없다). */
interface UaBrand {
  brand: string;
  version: string;
}
interface UaHighEntropy {
  platformVersion?: string;
  fullVersionList?: UaBrand[];
}
interface UaData {
  brands?: UaBrand[];
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<UaHighEntropy>;
}

type ParsedEnvironment = Pick<ClientEnvironment, "browser" | "browserVersion" | "os" | "osVersion">;

/** getHighEntropyValues 가 응답하지 않아도 알림을 붙잡지 않는다. */
const CLIENT_HINTS_TIMEOUT_MS = 1000;

// 순서가 중요하다 — Edge·Whale·Opera·삼성 인터넷은 UA 에 Chrome/ 도 싣고, Chrome 은 Safari/ 도 싣는다.
const UA_BROWSERS: Array<[ClientBrowser, RegExp]> = [
  [ClientBrowser.EDGE, /Edg(?:e|A|iOS)?\/([\d.]+)/],
  [ClientBrowser.WHALE, /Whale\/([\d.]+)/],
  [ClientBrowser.OPERA, /(?:OPR|Opera)\/([\d.]+)/],
  [ClientBrowser.SAMSUNG_INTERNET, /SamsungBrowser\/([\d.]+)/],
  [ClientBrowser.FIREFOX, /(?:Firefox|FxiOS)\/([\d.]+)/],
  [ClientBrowser.CHROME, /(?:Chrome|CriOS)\/([\d.]+)/],
  [ClientBrowser.SAFARI, /Version\/([\d.]+).*Safari\//],
];

// Client Hints 의 brand. "Google Chrome" 은 순수 Chrome 에만 붙고 파생 브라우저는 자기 이름을 단다.
const HINT_BRANDS: Record<string, ClientBrowser> = {
  "Microsoft Edge": ClientBrowser.EDGE,
  Whale: ClientBrowser.WHALE,
  Opera: ClientBrowser.OPERA,
  "Samsung Internet": ClientBrowser.SAMSUNG_INTERNET,
  "Google Chrome": ClientBrowser.CHROME,
  Chromium: ClientBrowser.CHROMIUM,
};

const HINT_PLATFORMS: Record<string, ClientOs> = {
  Windows: ClientOs.WINDOWS,
  macOS: ClientOs.MACOS,
  Android: ClientOs.ANDROID,
  "Chrome OS": ClientOs.CHROMEOS,
  Linux: ClientOs.LINUX,
};

/** UA 축소로 "140.0.0.0" 처럼 오는 버전은 뒷자리가 의미 없다 — 앞자리만 남긴다. */
const trimReducedVersion = (version: string): string => version.replace(/^(\d+)(?:\.0)+$/, "$1");

/** 1. 기본값 — 어느 브라우저에나 있는 User-Agent 문자열로 판정한다. */
export const parseUserAgent = (userAgent: string): ParsedEnvironment => {
  let browser = ClientBrowser.OTHER;
  let browserVersion: string | undefined;
  for (const [candidate, pattern] of UA_BROWSERS) {
    const match = pattern.exec(userAgent);
    if (match) {
      browser = candidate;
      browserVersion = trimReducedVersion(match[1]);
      break;
    }
  }

  const iosVersion = /OS (\d+)[_.](\d+)/.exec(userAgent);
  const androidVersion = /Android (\d+(?:\.\d+)?)/.exec(userAgent);
  let os = ClientOs.OTHER;
  let osVersion: string | undefined;
  if (/iPad/.test(userAgent)) {
    os = ClientOs.IPADOS;
    osVersion = iosVersion ? `${iosVersion[1]}.${iosVersion[2]}` : undefined;
  } else if (/iPhone|iPod/.test(userAgent)) {
    os = ClientOs.IOS;
    osVersion = iosVersion ? `${iosVersion[1]}.${iosVersion[2]}` : undefined;
  } else if (/Windows/.test(userAgent)) {
    // "Windows NT 10.0" 은 10 과 11 이 같다 — 버전은 Client Hints 가 있을 때만 싣는다.
    os = ClientOs.WINDOWS;
  } else if (/Macintosh|Mac OS X/.test(userAgent)) {
    // macOS 버전도 UA 에서는 10_15_7 로 고정돼 있어 쓸 수 없다.
    os = ClientOs.MACOS;
  } else if (androidVersion) {
    os = ClientOs.ANDROID;
    osVersion = androidVersion[1];
  } else if (/CrOS/.test(userAgent)) {
    os = ClientOs.CHROMEOS;
  } else if (/Linux/.test(userAgent)) {
    os = ClientOs.LINUX;
  }

  return { browser, browserVersion, os, osVersion };
};

/**
 * Windows 는 platformVersion 이 곧 OS 버전이 아니다 — 13 이상이 Windows 11, 1~10 이 Windows 10 이다.
 * (https://learn.microsoft.com/microsoft-edge/web-platform/how-to-detect-win11)
 */
const toOsVersion = (os: ClientOs, platformVersion: string): string | undefined => {
  const [major, minor] = platformVersion.split(".").map(Number);
  if (!Number.isFinite(major)) return undefined;
  if (os === ClientOs.WINDOWS) {
    if (major >= 13) return "11";
    return major >= 1 ? "10" : undefined;
  }
  if (os === ClientOs.MACOS) return `${major}.${Number.isFinite(minor) ? minor : 0}`;
  if (os === ClientOs.ANDROID) return String(major);
  return undefined;
};

/** 2. Chromium 계열이면 Client Hints 로 덮어쓴다 — UA 축소로 사라진 OS·브라우저 버전을 되찾는다. */
export const applyClientHints = (
  base: ParsedEnvironment,
  uaData: Pick<UaData, "brands" | "platform">,
  highEntropy?: UaHighEntropy
): ParsedEnvironment => {
  const result = { ...base };

  const brands = highEntropy?.fullVersionList?.length ? highEntropy.fullVersionList : uaData.brands ?? [];
  // 파생 브라우저(Edge 등)는 Chromium 과 자기 이름을 함께 싣는다 — 더 구체적인 쪽을 고른다.
  const known = brands
    .map(({ brand, version }) => ({ browser: HINT_BRANDS[brand], version }))
    .filter((entry): entry is { browser: ClientBrowser; version: string } => entry.browser !== undefined)
    .sort((a, b) => Number(a.browser === ClientBrowser.CHROMIUM) - Number(b.browser === ClientBrowser.CHROMIUM));
  if (known.length > 0) {
    result.browser = known[0].browser;
    result.browserVersion = trimReducedVersion(known[0].version);
  }

  const os = uaData.platform ? HINT_PLATFORMS[uaData.platform] : undefined;
  if (os) {
    result.os = os;
    result.osVersion = highEntropy?.platformVersion ? toOsVersion(os, highEntropy.platformVersion) : result.osVersion;
  }
  return result;
};

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("client hints timeout")), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

/**
 * 응시자 접속 환경을 모은다. 알림은 부수 신호라 **절대 throw 하지 않는다** — 여기서 던지면 호출부가
 * 알림 전송까지 함께 삼켜 버린다. 무엇이 실패하든 알아낸 데까지만 싣는다.
 */
export const collectClientEnvironment = async (): Promise<ClientEnvironment> => {
  const unknown: ClientEnvironment = { browser: ClientBrowser.OTHER, os: ClientOs.OTHER, deviceKind: DeviceKind.DESKTOP };
  if (typeof window === "undefined" || typeof window.navigator === "undefined") return unknown;

  try {
    const navigator = window.navigator;
    let parsed = parseUserAgent(navigator.userAgent ?? "");

    const uaData = (navigator as Navigator & { userAgentData?: UaData }).userAgentData;
    if (uaData) {
      let highEntropy: UaHighEntropy | undefined;
      try {
        highEntropy = await withTimeout(
          uaData.getHighEntropyValues?.(["platformVersion", "fullVersionList"]) ?? Promise.resolve({}),
          CLIENT_HINTS_TIMEOUT_MS
        );
      } catch {
        // 거부·시간 초과여도 low-entropy 값(brands, platform)은 쓸 수 있다.
      }
      parsed = applyClientHints(parsed, uaData, highEntropy);
    }

    const deviceKind = getDeviceKind();
    // iPadOS Safari 는 기본 설정에서 Mac UA 를 보낸다 — DeviceUtil 이 터치로 가려낸 판정을 따른다.
    if (deviceKind === DeviceKind.TABLET && parsed.os === ClientOs.MACOS) {
      parsed = { ...parsed, os: ClientOs.IPADOS, osVersion: undefined };
    }

    const appVersion = window.codepresso?.version || undefined;
    return { ...parsed, deviceKind, ...(appVersion ? { appVersion } : {}) };
  } catch {
    return unknown;
  }
};
